import { Link } from '@inertiajs/react'
import BlogSearchForm from './BlogSearchForm'
import type { CategoryNavItem } from './BlogCategoryNav'

interface BlogSidebarProps {
  categories: CategoryNavItem[]
  /** Slug of the currently-active category. Pass null/undefined on the
   *  blog index (where 'All articles' is the active state). */
  activeSlug?: string | null
  /** When true ('tag' archive pages), suppress the 'All articles' active
   *  state for the categories list so neither category appears selected. */
  hideActiveState?: boolean
}

/** Vertical category navigation for the blog index + archive pages.
 *  Renders as a stacked list of links; sticky at lg+ so it stays in
 *  view as the article grid scrolls. On mobile it's a horizontal pill
 *  row above the content. */
export default function BlogSidebar({
  categories,
  activeSlug = null,
  hideActiveState = false
}: BlogSidebarProps) {
  if (categories.length === 0) return null

  const allActive = !hideActiveState && !activeSlug

  return (
    <aside aria-label="Blog categories" className="lg:sticky lg:top-28 lg:self-start">
      {/* Mobile: search + horizontal pill row */}
      <div className="space-y-4 lg:hidden">
        <BlogSearchForm />
        <nav className="flex flex-wrap gap-2">
          <MobilePill href="/blogs" active={allActive}>All articles</MobilePill>
          {categories.map((c) => (
            <MobilePill
              key={c.slug}
              href={`/blogs/category/${c.slug}`}
              active={!hideActiveState && activeSlug === c.slug}
            >
              {c.name}
            </MobilePill>
          ))}
        </nav>
      </div>

      {/* Desktop: search + stacked sidebar */}
      <div className="hidden lg:block">
        <BlogSearchForm />
        <h2 className="mt-8 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-white/40">
          Browse
        </h2>
        <nav className="mt-4 space-y-1">
          <SidebarLink href="/blogs" active={allActive}>All articles</SidebarLink>
          {categories.map((c) => (
            <SidebarLink
              key={c.slug}
              href={`/blogs/category/${c.slug}`}
              active={!hideActiveState && activeSlug === c.slug}
            >
              {c.name}
            </SidebarLink>
          ))}
        </nav>
      </div>
    </aside>
  )
}

function SidebarLink({
  href,
  active,
  children
}: {
  href: string
  active: boolean
  children: React.ReactNode
}) {
  return (
    <Link
      href={href}
      prefetch
      aria-current={active ? 'page' : undefined}
      className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
        active
          ? 'bg-[#ff6a1a]/15 text-[#ff6a1a]'
          : 'text-white/60 hover:bg-white/[0.04] hover:text-white'
      }`}
    >
      {children}
    </Link>
  )
}

function MobilePill({
  href,
  active,
  children
}: {
  href: string
  active: boolean
  children: React.ReactNode
}) {
  return (
    <Link
      href={href}
      prefetch
      aria-current={active ? 'page' : undefined}
      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'bg-[#ff6a1a] text-[#1a0800]'
          : 'border border-white/10 text-white/64 hover:text-white'
      }`}
    >
      {children}
    </Link>
  )
}
